/** @jsxImportSource @emotion/react */
import { ReactNode, useCallback, useMemo, useState } from "react";
import {
  Background,
  ReactFlow,
  Panel,
  Position,
  Node,
  NodeTypes,
  Edge,
  NodeChange,
  XYPosition,
  Controls,
  MarkerType,
} from "@xyflow/react";
import dagre, { EdgeLabel, GraphLabel, NodeLabel } from "@dagrejs/dagre";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import "./workflow.css";
import { EuiSpacer } from "@inscopix/ideas-eui";
import { LayoutToggle } from "./LayoutToggle";
import { workflowDiagramStyles } from "./workflowDiagramStyles";

const position = { x: 0, y: 0 };

type LayoutDirection = "TB" | "LR";
type Size = { width: number; height: number };

const nodeWidth = 200;
const nodeHeight = 50;

const getLayoutedPositions = (
  nodes: Pick<Node, "id">[],
  edges: Pick<Edge, "source" | "target">[],
  direction: LayoutDirection,
): Record<string, XYPosition> => {
  const dagreGraph = new dagre.graphlib.Graph<
    GraphLabel,
    NodeLabel,
    EdgeLabel
  >().setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: direction });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const positions: Record<string, XYPosition> = {};
  nodes.forEach((node) => {
    const p = dagreGraph.node(node.id);
    positions[node.id] = {
      x: (p?.x ?? 0) - nodeWidth / 2,
      y: (p?.y ?? 0) - nodeHeight / 2,
    };
  });
  return positions;
};

export interface DiagramNode<T extends string> {
  id: string;
  type?: T;
  data: Record<string, unknown>;
}

export interface GraphProps<T extends NodeTypes> {
  nodeTypes?: T;
  nodes: DiagramNode<Extract<keyof T, string>>[];
  edges: Pick<Edge, "id" | "source" | "target">[];
  legend?: ReactNode;
}

export const Graph = <TNodeTypes extends NodeTypes>({
  nodeTypes,
  nodes: inputNodes,
  edges: inputEdges,
  legend,
}: GraphProps<TNodeTypes>) => {
  const [direction, setDirection] = useState<LayoutDirection>("TB");
  const [dragged, setDragged] = useState<Record<string, XYPosition>>({});
  const [measured, setMeasured] = useState<Record<string, Size>>({});

  const builtNodes = useMemo<Node[]>(
    () => inputNodes.map((n) => ({ ...n, position })),
    [inputNodes],
  );

  const edges = useMemo<Edge[]>(
    () =>
      inputEdges.map((e) => ({
        ...e,
        markerEnd: {
          type: MarkerType.Arrow,
          width: 10,
          height: 10,
          color: workflowDiagramStyles.colors.edge,
        },
        style: { strokeWidth: 1.5, stroke: workflowDiagramStyles.colors.edge },
      })),
    [inputEdges],
  );

  const autoPositions = useMemo(
    () => getLayoutedPositions(builtNodes, edges, direction),
    [builtNodes, edges, direction],
  );

  const nodes = useMemo<Node[]>(() => {
    const isHorizontal = direction === "LR";
    return builtNodes.map((n) => ({
      ...n,
      style: { ...n.style, padding: 5 },
      measured: measured[n.id],
      targetPosition: isHorizontal ? Position.Left : Position.Top,
      sourcePosition: isHorizontal ? Position.Right : Position.Bottom,
      position: dragged[n.id] ?? autoPositions[n.id] ?? n.position,
    }));
  }, [builtNodes, dragged, measured, autoPositions, direction]);

  const onNodesChange = useCallback((changes: NodeChange[]) => {
    setDragged((prev) => {
      let next = prev;
      for (const change of changes) {
        if (change.type === "position" && change.position) {
          if (next === prev) next = { ...prev };
          next[change.id] = change.position;
        }
      }
      return next;
    });

    setMeasured((prev) => {
      let next = prev;
      for (const change of changes) {
        if (change.type === "dimensions" && change.dimensions) {
          if (next === prev) next = { ...prev };
          next[change.id] = change.dimensions;
        }
      }
      return next;
    });
  }, []);

  const onLayout = useCallback((nextDirection: LayoutDirection) => {
    setDragged({});
    setDirection(nextDirection);
  }, []);

  return (
    <ReactFlow
      style={{ height: "100%", width: "100%" }}
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes}
      nodesConnectable={false}
      onNodesChange={onNodesChange}
      fitView
      fitViewOptions={{ padding: 0.2 }}
      colorMode="light"
    >
      <Controls />

      <Panel position="top-left">
        <LayoutToggle value={direction} onChange={onLayout} />
        {legend !== undefined && (
          <>
            <EuiSpacer />
            {legend}
          </>
        )}
      </Panel>
      <Background />
    </ReactFlow>
  );
};
